if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        progressNav: this.querySelector('[data-media-progress]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      this.setupMediaProgress();
    }

    get usesInternalScroll() {
      return Boolean(this.scrollContainer && this.dataset.desktopLayout === 'stacked' && window.matchMedia('(min-width: 990px)').matches);
    }

    disconnectedCallback() {
      if (this.onWindowScroll) {
        window.removeEventListener('scroll', this.onWindowScroll);
        window.removeEventListener('resize', this.onWindowScroll);
      }

      if (this.scrollContainer && this.onContainerScroll) {
        this.scrollContainer.removeEventListener('scroll', this.onContainerScroll);
      }

      if (this.mediaList && this.onMediaListScroll) {
        this.mediaList.removeEventListener('scroll', this.onMediaListScroll);
      }

      if (this.progressButtons) {
        if (this.progressButtons.prev && this.onProgressPrevClick) {
          this.progressButtons.prev.removeEventListener('click', this.onProgressPrevClick);
        }
        if (this.progressButtons.next && this.onProgressNextClick) {
          this.progressButtons.next.removeEventListener('click', this.onProgressNextClick);
        }
      }
    }

    onSlideChanged(event) {
      if (this.elements.thumbnails) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }

      this.updateMediaProgressByMediaId(event.detail.currentElement.dataset.mediaId);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (this.usesInternalScroll && this.scrollContainer) {
          const centeredTop = activeMedia.offsetTop - ((this.scrollContainer.clientHeight - activeMedia.offsetHeight) / 2);
          this.scrollContainer.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
        } else if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setupMediaProgress() {
      if (!this.elements.progressNav) return;

      this.progressItems = this.getProgressMediaItems();
      if (this.progressItems.length < 2) {
        this.elements.progressNav.setAttribute('hidden', 'hidden');
        return;
      }

      this.progressCurrent = this.elements.progressNav.querySelector('[data-media-progress-current]');
      this.progressTotal = this.elements.progressNav.querySelector('[data-media-progress-total]');
      this.progressTrack = this.elements.progressNav.querySelector('[data-media-progress-track]');
      this.progressFill = this.elements.progressNav.querySelector('[data-media-progress-fill]');

      this.progressButtons = {
        prev: this.elements.progressNav.querySelector('[data-media-progress-prev]'),
        next: this.elements.progressNav.querySelector('[data-media-progress-next]')
      };

      this.onProgressPrevClick = this.onProgressPrevClick || (() => this.moveMediaProgress(-1));
      this.onProgressNextClick = this.onProgressNextClick || (() => this.moveMediaProgress(1));
      this.onWindowScroll = this.onWindowScroll || debounce(this.updateMediaProgressFromScroll.bind(this), 50);
      this.onContainerScroll = this.onContainerScroll || debounce(this.updateMediaProgressFromScroll.bind(this), 50);
      this.onMediaListScroll = this.onMediaListScroll || this.updateMediaProgressFromScroll.bind(this);
      this.scrollContainer = this.elements.viewer.querySelector('.product-media-scroll-container');
      this.progressWrap = this.elements.viewer.querySelector('.product-media-progress-wrap');
      this.mediaList = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');

      if (this.progressButtons.prev) {
        this.progressButtons.prev.addEventListener('click', this.onProgressPrevClick);
      }
      if (this.progressButtons.next) {
        this.progressButtons.next.addEventListener('click', this.onProgressNextClick);
      }

      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('resize', this.onWindowScroll);
      if (this.scrollContainer) {
        this.scrollContainer.addEventListener('scroll', this.onContainerScroll, { passive: true });
      }
      if (this.mediaList) {
        this.mediaList.addEventListener('scroll', this.onMediaListScroll, { passive: true });
      }

      if (this.progressTotal) {
        this.progressTotal.textContent = this.progressItems.length;
      }

      this.syncProgressWrapHeight();
      window.setTimeout(() => this.syncProgressWrapHeight(), 0);
      window.setTimeout(() => this.syncProgressWrapHeight(), 600);

      this.updateMediaProgress(this.getCurrentMediaIndex());
    }

    syncProgressWrapHeight() {
      if (!this.progressWrap || !this.mediaList) return;

      const listHeight = this.mediaList.scrollHeight;
      if (!listHeight) return;

      this.progressWrap.style.height = `${listHeight}px`;
    }

    getCurrentMediaIndex() {
      if (!this.progressItems || !this.progressItems.length) return 0;

      const activeIndex = this.progressItems.findIndex((item) => item.classList.contains('is-active'));
      if (activeIndex > -1) return activeIndex;

      return this.getVisibleMediaIndex();
    }

    getInternalScrollRatio() {
      if (!this.usesInternalScroll || !this.scrollContainer) return null;

      const maxScrollTop = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight;
      if (maxScrollTop <= 0) return 0;

      const ratio = this.scrollContainer.scrollTop / maxScrollTop;
      return Math.min(Math.max(ratio, 0), 1);
    }

    getVisibleMediaIndex() {
      if (!this.progressItems || !this.progressItems.length) return 0;

      if (this.usesInternalScroll && this.scrollContainer) {
        const maxIndex = this.progressItems.length - 1;
        const maxScrollTop = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight;

        if (this.scrollContainer.scrollTop <= 2) return 0;
        if (maxScrollTop > 0 && this.scrollContainer.scrollTop >= maxScrollTop - 2) return maxIndex;

        const containerRect = this.scrollContainer.getBoundingClientRect();
        const containerReference = containerRect.top + (containerRect.height * 0.5);
        let closestIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        this.progressItems.forEach((item, index) => {
          if (item.offsetParent === null) return;
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.top + (itemRect.height * 0.5);
          const distance = Math.abs(itemCenter - containerReference);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      if (this.mediaList && this.mediaList.scrollWidth > this.mediaList.clientWidth) {
        const maxScrollLeft = this.mediaList.scrollWidth - this.mediaList.clientWidth;
        const maxIndex = this.progressItems.length - 1;

        if (this.mediaList.scrollLeft <= 2) return 0;
        if (maxScrollLeft > 0 && this.mediaList.scrollLeft >= maxScrollLeft - 2) return maxIndex;

        const listRect = this.mediaList.getBoundingClientRect();
        const listReference = listRect.left + (listRect.width * 0.5);
        let closestIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;

        this.progressItems.forEach((item, index) => {
          if (item.offsetParent === null) return;
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + (itemRect.width * 0.5);
          const distance = Math.abs(itemCenter - listReference);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      const viewportReference = Math.max(120, window.innerHeight * 0.3);
      let closestIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      this.progressItems.forEach((item, index) => {
        if (item.offsetParent === null) return;
        const rect = item.getBoundingClientRect();
        const distance = Math.abs(rect.top - viewportReference);

        if (distance < smallestDistance) {
          smallestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    updateMediaProgressFromScroll() {
      if (!this.progressItems || this.progressItems.length < 2) return;
      this.progressItems = this.getProgressMediaItems();
      this.syncProgressWrapHeight();

      if (this.usesInternalScroll) {
        const ratio = this.getInternalScrollRatio();
        const maxIndex = this.progressItems.length - 1;
        const ratioIndex = Math.round((ratio || 0) * maxIndex);
        this.updateMediaProgress(ratioIndex, ratio);
        return;
      }

      if (this.mediaList && this.mediaList.scrollWidth > this.mediaList.clientWidth) {
        const maxScrollLeft = this.mediaList.scrollWidth - this.mediaList.clientWidth;
        this.updateMediaProgress(this.getVisibleMediaIndex(), this.mediaList.scrollLeft / maxScrollLeft);
        return;
      }

      this.updateMediaProgress(this.getVisibleMediaIndex());
    }

    getProgressMediaItems() {
      const mediaList = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
      if (!mediaList) return [];

      return Array.from(mediaList.children).filter((item) => item.hasAttribute('data-media-id') && item.offsetParent !== null);
    }

    updateMediaProgressByMediaId(mediaId) {
      if (!this.progressItems || !mediaId) return;
      const mediaIndex = this.progressItems.findIndex((item) => item.dataset.mediaId === mediaId);
      if (mediaIndex === -1) return;

      this.updateMediaProgress(mediaIndex);
    }

    updateMediaProgress(index, ratioOverride = null) {
      if (!this.progressItems || !this.progressItems.length) return;

      const maxIndex = this.progressItems.length - 1;
      const clampedIndex = Math.min(Math.max(index, 0), maxIndex);
      this.currentMediaProgressIndex = clampedIndex;

      if (this.progressCurrent) {
        this.progressCurrent.textContent = clampedIndex + 1;
      }

      if (this.progressTrack) {
        this.progressTrack.setAttribute('aria-valuenow', clampedIndex + 1);
      }

      if (this.progressFill) {
        const ratio = typeof ratioOverride === 'number'
          ? Math.min(Math.max(ratioOverride, 0), 1)
          : (maxIndex > 0 ? (clampedIndex / maxIndex) : 0);
        const trackStyles = this.progressTrack ? window.getComputedStyle(this.progressTrack) : null;
        const isMobileProgress = window.matchMedia('(max-width: 989px)').matches;

        if (isMobileProgress) {
          const railWidth = trackStyles ? parseFloat(trackStyles.getPropertyValue('--progress-rail-width')) : 0;
          const thumbWidth = this.progressFill.clientWidth;
          const travel = Math.max(0, railWidth - thumbWidth);
          const offsetX = travel * ratio;
          this.progressFill.style.transform = `translate(${offsetX}px, -50%)`;
        } else {
          const railHeight = trackStyles ? parseFloat(trackStyles.getPropertyValue('--progress-rail-height')) : 0;
          const thumbHeight = this.progressFill.clientHeight;
          const travel = Math.max(0, railHeight - thumbHeight);
          const offsetY = travel * ratio;
          this.progressFill.style.transform = `translate(-50%, ${offsetY}px)`;
        }
      }

      if (this.progressButtons) {
        if (this.progressButtons.prev) {
          this.progressButtons.prev.toggleAttribute('disabled', clampedIndex === 0);
        }
        if (this.progressButtons.next) {
          this.progressButtons.next.toggleAttribute('disabled', clampedIndex === maxIndex);
        }
      }
    }

    moveMediaProgress(direction) {
      if (!this.progressItems || !this.progressItems.length) return;

      const baseIndex = typeof this.currentMediaProgressIndex === 'number' ? this.currentMediaProgressIndex : this.getCurrentMediaIndex();
      const targetIndex = Math.min(Math.max(baseIndex + direction, 0), this.progressItems.length - 1);
      if (targetIndex === baseIndex) return;

      const targetMedia = this.progressItems[targetIndex];
      if (!targetMedia) return;

      this.setActiveMedia(targetMedia.dataset.mediaId, false);
      this.updateMediaProgress(targetIndex);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
